import { toast } from 'sonner';
import { useEffect, useState } from 'react';
import { format, isSameDay, startOfWeek } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useMatch, useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import {
  ValidationError,
  useAuthUserRestaurant,
  useCopyWeekMenusMutation,
  useGetRestaurantWeekMenusQuery,
} from '@durma-soft/gros-sdk';

import { Modal } from '@/components/shared/modal';
import { Label } from '@/components/shared/shadcn-ui/label';
import { Switch } from '@/components/shared/shadcn-ui/switch';
import { Button } from '@/components/shared/shadcn-ui/button';
import { WeekPicker } from '@/components/shared/week-picker';
import { ConfirmationModal } from '@/components/shared/confirmation-modal';
import { CopyRestaurantWeekMenuPreview } from '@/components/restaurant/menus/copy-restaurant-week-menu-preview';

export const CopyRestaurantWeekMenuModal = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const [fromWeekStart, setFromWeekStart] = useState<Date | undefined>();
  const [toWeekStart, setToWeekStart] = useState<Date | undefined>();
  const [isActive, setIsActive] = useState(false);
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] = useState(false);

  const restaurant = useAuthUserRestaurant();
  const copyWeekMenus = useCopyWeekMenusMutation();

  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });

  const weekMenus = useGetRestaurantWeekMenusQuery({
    restaurant_id: restaurant.id,
    week_start: fromWeekStart ? format(fromWeekStart, 'yyyy-MM-dd') : '',
  });

  const isModalOpen = Boolean(
    useMatch({ path: '/restaurant/menus/copy/week' }),
  );

  const resetState = () => {
    setFromWeekStart(undefined);
    setToWeekStart(undefined);
    setIsActive(false);
    setIsConfirmationModalOpen(false);
  };

  // The modal is URL-driven, so browser back/forward can close it without closeModal running.
  useEffect(() => {
    if (!isModalOpen) resetState();
  }, [isModalOpen]);

  const closeModal = () => {
    navigate(`/restaurant/menus?${searchParams.toString()}`, { replace: true });
    resetState();
  };

  const isSourceWeekEmpty =
    !weekMenus.data ||
    Object.values(weekMenus.data).every((day) => Object.keys(day).length === 0);

  const showSkipNote = Boolean(
    toWeekStart &&
      isSameDay(toWeekStart, currentWeekStart) &&
      !isSameDay(new Date(), currentWeekStart),
  );

  const isValid = Boolean(
    fromWeekStart &&
      toWeekStart &&
      !isSameDay(fromWeekStart, toWeekStart) &&
      !isSourceWeekEmpty,
  );

  const handleCopy = async () => {
    if (!fromWeekStart || !toWeekStart) return;
    try {
      const result = await copyWeekMenus.mutateAsync({
        restaurant_id: restaurant.id,
        from_week_start: format(fromWeekStart, 'yyyy-MM-dd'),
        to_week_start: format(toWeekStart, 'yyyy-MM-dd'),
        is_active: isActive,
      });
      toast.success(
        result.skipped_days.length > 0
          ? t('menu.copy.week-success-skipped-msg')
          : t('menu.copy.week-success-msg'),
      );
      closeModal();
    } catch (error) {
      if (error instanceof ValidationError) {
        toast.error(error.message);
      } else {
        toast.error(t('menu.copy.week-error-msg'));
      }
    } finally {
      setIsConfirmationModalOpen(false);
    }
  };

  return (
    <Modal
      isModalOpen={isModalOpen}
      closeModal={closeModal}
      title={
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => {
              resetState();
              navigate(`/restaurant/menus/copy?${searchParams.toString()}`, {
                replace: true,
              });
            }}
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          {t('menu.copy.week-title')}
        </div>
      }
      className="flex flex-col gap-6 overflow-hidden sm:max-w-[calc(100%-4rem)] lg:max-w-[768px] xl:max-w-[1024px]"
    >
      <div className="flex flex-wrap gap-4">
        <div className="flex flex-col gap-2">
          <Label>{t('menu.copy.from-week')}</Label>
          <WeekPicker
            id="from-week-picker"
            weekStart={fromWeekStart}
            setWeekStart={setFromWeekStart}
          />
        </div>
        <div className="flex flex-col gap-2">
          <Label>{t('menu.copy.to-week')}</Label>
          <WeekPicker
            id="to-week-picker"
            weekStart={toWeekStart}
            setWeekStart={setToWeekStart}
            disabledBefore={currentWeekStart}
          />
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <Label htmlFor="week-copy-is-active">{t('menu.activate-all')}</Label>
        <Switch
          id="week-copy-is-active"
          checked={isActive}
          onCheckedChange={setIsActive}
        />
      </div>
      {showSkipNote && (
        <p className="text-sm text-muted-foreground">
          {t('menu.copy.skip-note')}
        </p>
      )}
      {Boolean(fromWeekStart) &&
        isSourceWeekEmpty &&
        !weekMenus.isLoading &&
        !weekMenus.isError && (
        <p className="text-sm text-destructive">
          {t('menu.copy.empty-source-week')}
        </p>
      )}
      <div className="max-h-[45vh] flex-1 overflow-y-auto p-2">
        <CopyRestaurantWeekMenuPreview
          hasSourceWeek={Boolean(fromWeekStart)}
          data={weekMenus.data}
          isLoading={weekMenus.isLoading}
          isError={weekMenus.isError}
        />
      </div>
      <div className="mt-2 flex justify-end gap-2">
        <Button onClick={closeModal} variant="outline">
          {t('common.cancel')}
        </Button>
        <Button
          disabled={!isValid}
          onClick={() => setIsConfirmationModalOpen(true)}
        >
          {t('common.copy')}
        </Button>
      </div>
      <ConfirmationModal
        isLoading={copyWeekMenus.isPending}
        isModalOpen={isConfirmationModalOpen}
        title={t('menu.copy.week-title')}
        description={t('menu.copy.week-confirm-description')}
        confirmationText={t('common.copy')}
        onConfirm={handleCopy}
        onCancel={() => setIsConfirmationModalOpen(false)}
        closeModal={() => setIsConfirmationModalOpen(false)}
      />
    </Modal>
  );
};
